import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardBody, Spinner } from '@heroui/react';
import { useAuth } from '../../contexts/AuthContext';
import { useStomp } from '../../contexts/StompContext';
import PostEditor from './PostEditor';
import Post from './Post';
import SecurityMap from './SecurityMap';

const Timeline = ({ category: externalCategory, onPostsUpdate, visibilityParams = {} }) => {
  const { user } = useAuth();
  const { addMessageHandler, isConnected } = useStomp();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nextPage, setNextPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [category, setCategory] = useState(externalCategory || 'Geral');

  // Handle real-time timeline posts via STOMP (using the new message type system)
  const handleTimelineMessage = useCallback((message, topic) => {
    console.log('Timeline: Received timeline message from', topic, message);

    // Handle post deletion
    if (message.action === 'delete' && message.post) {
      console.log('Timeline: Removing deleted post:', message.post.id);
      setPosts(prevPosts => {
        return prevPosts.filter(post => {
          const postId = post._id?.['$oid'] || post._id;
          return postId !== message.post.id;
        });
      });
      return;
    }

    // Handle new posts
    if (message.post && message.user) {
      console.log('Timeline: Adding new post from STOMP:', message.post.title);
      console.log('Timeline: Post ID structure:', message.post._id);

      setPosts(prevPosts => {
        // console.log('Timeline: Previous posts count:', prevPosts.length);

        // More robust duplicate detection
        const getPostId = (post) => {
          if (!post._id) return null;
          if (typeof post._id === 'string') return post._id;
          if (post._id['$oid']) return post._id['$oid'];
          return post._id.toString();
        };

        const newPostId = getPostId(message.post);
        console.log('Timeline: New post ID:', newPostId);

        if (newPostId) {
          const existingPost = prevPosts.find(p => {
            const existingId = getPostId(p);
            return existingId === newPostId;
          });

          if (existingPost) {
            console.log('Timeline: Post already exists, skipping duplicate');
            return prevPosts;
          }
        }

        // console.log('Timeline: Adding new post to timeline');
        const newPosts = [message.post, ...prevPosts];
        // console.log('Timeline: New posts count:', newPosts.length);
        // console.log('Timeline: State update completed');
        return newPosts;
      });
    }

    // Handle post updates
    if (message.post_update?.post) {
      const updatedPost = message.post_update.post;
      console.log('Timeline: Updating post from STOMP:', updatedPost.title);

      setPosts(prevPosts =>
        prevPosts.map(post => {
          const postId = post._id?.['$oid'] || post._id;
          const updatedPostId = updatedPost._id?.['$oid'] || updatedPost._id;
          return postId === updatedPostId ? updatedPost : post;
        })
      );
    }

    // Handle post deletions
    if (message.post_delete?.post_id) {
      const deletedPostId = message.post_delete.post_id;
      console.log('Timeline: Removing deleted post from STOMP:', deletedPostId);

      setPosts(prevPosts =>
        prevPosts.filter(post => {
          const postId = post._id?.['$oid'] || post._id;
          return postId !== deletedPostId;
        })
      );
    }

    // Handle poll updates
    if (message.type === 'poll_update' && message.post_id && message.poll_options) {
      console.log('Timeline: Updating poll data from STOMP for post:', message.post_id);

      setPosts(prevPosts =>
        prevPosts.map(post => {
          const postId = post._id?.['$oid'] || post._id;
          if (postId === message.post_id) {
            return {
              ...post,
              poll_options: message.poll_options
            };
          }
          return post;
        })
      );
    }

    // Handle post deletions
    if (message.post_delete?.post_id) {
      const deletedPostId = message.post_delete.post_id;
      console.log('Timeline: Removing post from STOMP:', deletedPostId);

      setPosts(prevPosts =>
        prevPosts.filter(post => {
          const postId = post._id?.['$oid'] || post._id;
          return postId !== deletedPostId;
        })
      );
    }

    // Handle comment deletions
    if (message.comment_delete?.post) {
      const postId = message.comment_delete.post._id?.['$oid'] || message.comment_delete.post._id;
      console.log('Timeline: Handling comment deletion for post:', postId);

      setPosts(prevPosts =>
        prevPosts.map(post => {
          const currentPostId = post._id?.['$oid'] || post._id;
          if (currentPostId === postId) {
            // Decrement comment count
            const newCommentCount = Math.max(0, (post.comment_count || 0) - 1);
            return {
              ...post,
              comment_count: newCommentCount
            };
          }
          return post;
        })
      );
    }

    // } else {
      // console.log('Timeline: Invalid message structure - missing post or user');
      // console.log('Timeline: message.post exists:', !!message.post);
      // console.log('Timeline: message.user exists:', !!message.user);
    // }
  }, []); // Empty dependency array is correct here since we use setPosts with function form

  // Subscribe to timeline messages when STOMP is ready
  useEffect(() => {
    // console.log('Timeline: STOMP subscription effect triggered');
    // console.log('Timeline: isConnected:', isConnected);
    // console.log('Timeline: addMessageHandler available:', typeof addMessageHandler);

    if (isConnected && addMessageHandler) {
      // console.log('Timeline: Setting up STOMP timeline handler');

      // Handle new posts
      const timelineCleanup = addMessageHandler('timeline', handleTimelineMessage);

      // Handle comment updates to update post comment counts
      const handleCommentUpdate = (message, topic) => {
        console.log('Timeline: Received comment update:', message);

        let postId = null;

        // Handle comment creation (comment_update)
        if (message.comment_update?.post) {
          postId = message.comment_update.post._id?.['$oid'] || message.comment_update.post._id;

          console.log('Timeline: Updating comment count for post', postId);

          // Update the post with fresh data from backend (includes updated comment counts)
          setPosts(prevPosts =>
            prevPosts.map(post => {
              const currentPostId = post._id?.['$oid'] || post._id;
              if (currentPostId === postId) {
                return {
                  ...post,
                  ...message.comment_update.post, // Use fresh data from backend
                  comment_total: message.comment_update.post.comment_total,
                  comment_total_full: message.comment_update.post.comment_total_full
                };
              }
              return post;
            })
          );
        }
        // Handle comment deletion (comment_delete)
        else if (message.comment_delete?.post) {
          postId = message.comment_delete.post._id?.['$oid'] || message.comment_delete.post._id;

          console.log('Timeline: Updating comment count for post after deletion', postId);

          // Update the post with fresh data from backend (includes updated comment counts)
          setPosts(prevPosts =>
            prevPosts.map(post => {
              const currentPostId = post._id?.['$oid'] || post._id;
              if (currentPostId === postId) {
                return {
                  ...post,
                  ...message.comment_delete.post, // Use fresh data from backend
                  comment_total: message.comment_delete.post.comment_total,
                  comment_total_full: message.comment_delete.post.comment_total_full
                };
              }
              return post;
            })
          );
        } else {
          console.log('Timeline: Ignoring notification format message (handled by Post components)');
        }
      };

      const commentCleanup = addMessageHandler('comment', handleCommentUpdate);

      // Handle vote updates to update post vote counts
      const handleVoteUpdate = (message, topic) => {
        console.log('Timeline: Received vote update:', message);

        let postId = null;
        let voteData = null;

        // Handle the simplified vote_update format
        if (message.vote_update?.post) {
          postId = message.vote_update.post._id?.['$oid'] || message.vote_update.post._id;
          voteData = {
            upvotes: message.vote_update.upvotes,
            downvotes: message.vote_update.downvotes,
            direction: message.vote_update.direction
          };
        }

        if (postId && voteData) {
          console.log('Timeline: Updating vote counts for post', postId);
          console.log('Timeline: Vote data:', voteData);

          // Update the vote counts for the specific post
          setPosts(prevPosts =>
            prevPosts.map(post => {
              const currentPostId = post._id?.['$oid'] || post._id;
              if (currentPostId === postId) {
                return {
                  ...post,
                  upvotes: voteData.upvotes || post.upvotes || 0,
                  downvotes: voteData.downvotes || post.downvotes || 0
                };
              }
              return post;
            })
          );
        }
      };

      const voteCleanup = addMessageHandler('vote', handleVoteUpdate);

      // console.log('Timeline: Timeline handler registered, cleanup function:', typeof cleanup);

      return () => {
        timelineCleanup();
        commentCleanup();
        voteCleanup();
      };
    // } else {
      // console.log('Timeline: Not setting up handler - isConnected:', isConnected, 'addMessageHandler:', !!addMessageHandler);
    }
  }, [isConnected, addMessageHandler]);

  // Handle post updates
  const handlePostUpdated = useCallback((updatedPost) => {
    console.log('Timeline: Handling post update:', updatedPost);
    setPosts(prevPosts =>
      prevPosts.map(post => {
        const postId = post._id?.['$oid'] || post._id;
        const updatedPostId = updatedPost._id?.['$oid'] || updatedPost._id;
        return postId === updatedPostId ? updatedPost : post;
      })
    );
  }, []);

  // Handle post deletions
  const handlePostDeleted = useCallback((deletedPostId) => {
    console.log('Timeline: Handling post deletion:', deletedPostId);
    setPosts(prevPosts =>
      prevPosts.filter(post => {
        const postId = post._id?.['$oid'] || post._id;
        return postId !== deletedPostId;
      })
    );
  }, []);

  const fetchPosts = useCallback(async (reset = false) => {
    try {
      if (reset) {
        setLoading(true);
      } else {
        setIsFetching(true);
      }

      const page = reset ? 1 : nextPage;
      const categoryParam = category !== 'Geral' ? `&post_category=${category}` : '';
      
      // Build visibility parameters
      const visibilityParam = Object.keys(visibilityParams).length > 0 
        ? `&${Object.entries(visibilityParams).map(([key, value]) => `${key}=${value}`).join('&')}`
        : '';

      console.log('Timeline: Fetching posts, page:', page, 'category:', category, 'visibility:', visibilityParams);

      const response = await fetch(
        `/api/v1/posts?include_stats=true&include_comments=true&page=${page}${categoryParam}${visibilityParam}`,
        { credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();
        const newPosts = data.posts || [];

        console.log('Timeline: Fetched', newPosts.length, 'posts');

        if (reset) {
          setPosts(newPosts);
          setNextPage(2);
        } else {
          setPosts(prev => [...prev, ...newPosts]);
          setNextPage(page + 1);
        }

        setHasMore(newPosts.length > 0);
      } else {
        console.error('Timeline: Failed to fetch posts', response.status);
      }
    } catch (error) {
      console.error('Timeline: Error fetching posts:', error);
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  }, [category, nextPage, visibilityParams]);

  // Fetch initial posts
  useEffect(() => {
    fetchPosts(true);
  }, [fetchPosts, category, visibilityParams]);

  // Handle new post created (don't add directly - let STOMP handle it)
  const handleNewPost = (newPost) => {
    console.log('Timeline: New post created via editor:', newPost?.title);
    console.log('Timeline: Waiting for STOMP message to add to timeline...');
    // Don't add here - let STOMP handle it to prevent duplication
  };

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    // Safety check to ensure DOM elements exist
    if (!document.documentElement || !window) {
      return;
    }

    if (
      window.innerHeight + document.documentElement.scrollTop
      >= document.documentElement.offsetHeight - 1000
      && hasMore && !isFetching && !loading
    ) {
      console.log('Timeline: Loading more posts...');
      fetchPosts();
    }
  }, [hasMore, isFetching, loading, fetchPosts]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  // Additional cleanup when category changes to prevent stale event handlers
  useEffect(() => {
    return () => {
      // Cleanup any pending scroll operations when category changes
      setIsFetching(false);
    };
  }, [category]);

  // Update category when external category changes
  useEffect(() => {
    if (externalCategory !== undefined && externalCategory !== category) {
      // Reset scroll-related state when category changes
      setHasMore(true);
      setIsFetching(false);
      setNextPage(1);
      setCategory(externalCategory);
    }
  }, [externalCategory, category]);

  // Call onPostsUpdate when posts change
  useEffect(() => {
    if (onPostsUpdate && posts.length > 0) {
      onPostsUpdate(posts);
    }
  }, [posts, onPostsUpdate]);

  if (loading && posts.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner size="lg" color="primary" />
      </div>
    );
  }
      // <StompDebug />

  return (
    <div className="space-y-6">
      {/* Debug Panel - Remove this in production */}

      {/* Connection Status */}
      {!isConnected && (
        <Card className="border-warning-200 bg-warning-50">
          <CardBody className="py-3">
            <div className="flex items-center space-x-2 text-warning-700">
              <div className="w-2 h-2 bg-warning-500 rounded-full animate-pulse"></div>
              <span className="text-sm">Reconectando ao sistema de tempo real...</span>
            </div>
          </CardBody>
        </Card>
      )}

      {/* STOMP Debug Info - Removed redundant status bar since header shows Online indicator */}

      {/* Post Editor */}
      <Card>
        <CardBody>
          <PostEditor onPostCreated={handleNewPost} />
        </CardBody>
      </Card>

      {/* Security Map - Show only for Security category */}
      {category === 'Segurança' && (
        <SecurityMap
          posts={posts.filter(post =>
            post &&
            post._id &&
            post.user &&
            post.location &&
            Array.isArray(post.location) &&
            post.location.length >= 2
          )}
          onPostClick={(post) => {
            // Scroll to the post when clicked on map
            const postElement = document.querySelector(`[data-post-id="${post._id?.['$oid'] || post._id}"]`);
            if (postElement) {
              postElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
              // Add a brief highlight effect
              postElement.classList.add('ring-2', 'ring-red-500', 'ring-opacity-50');
              setTimeout(() => {
                postElement.classList.remove('ring-2', 'ring-red-500', 'ring-opacity-50');
              }, 2000);
            }
          }}
        />
      )}

      {/* Posts */}
      <div className="space-y-4">
        {posts
          .filter(post => post && post._id && post.user) // Filter out invalid posts
          .map((post, index) => (
          <div key={post._id?.['$oid'] || post._id || index} className="post-enter">
            <Post
              post={post}
              onPostUpdated={handlePostUpdated}
              onPostDeleted={handlePostDeleted}
            />
          </div>
        ))}

        {posts.length === 0 && !loading && (
          <Card>
            <CardBody className="text-center py-12">
              <div className="space-y-4">
                <div className="text-6xl">🏛️</div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">
                    Nenhuma postagem encontrada
                  </h3>
                  <p className="text-gray-500">
                    Seja o primeiro a compartilhar algo com sua comunidade!
                  </p>
                </div>
              </div>
            </CardBody>
          </Card>
        )}

        {isFetching && posts.length > 0 && (
          <div className="flex justify-center py-4">
            <Spinner color="primary" />
          </div>
        )}

        {!hasMore && posts.length > 0 && (
          <Card>
            <CardBody className="text-center py-6">
              <p className="text-gray-500 text-sm">
                🎉 Você viu todas as postagens!
              </p>
            </CardBody>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Timeline;
